import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import type { Product, Location } from '../../types';

interface InventoryFormData {
  productId: string;
  locationId: string;
  locationType: 'store' | 'warehouse';
  initialQuantity: number;
  lowStockThreshold: number;
}

const fetchProducts = async (): Promise<Product[]> => {
  const response = await axiosInstance.get('/products');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations/admin/all');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

export const InventoryCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showMessage } = useSnackbar();

  // Pre-select product if passed via URL
  const preselectedProductId = searchParams.get('productId');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm<InventoryFormData>({
    defaultValues: {
      productId: preselectedProductId || '',
      locationId: '',
      locationType: 'store',
      initialQuantity: 0,
      lowStockThreshold: 10,
    },
  });

  const locationType = watch('locationType');

  // Fetch data for dropdowns
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
  });

  const { data: allLocations = [], isLoading: locationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
  });

  // Filter locations based on selected type
  type LocationOption = { id: string; name: string };
  const locations: LocationOption[] = allLocations
    .filter(loc => loc.type === locationType)
    .map(loc => ({ id: loc.id, name: loc.name }));

  const createMutation = useMutation({
    mutationFn: (data: InventoryFormData) => {
      // Exclude locationType from submission - it's only used for UI filtering
      const { locationType: _locationType, ...submitData } = data;
      return axiosInstance.post('/inventory', submitData);
    },
    onSuccess: () => {
      showMessage('Inventory created successfully', 'success');
      navigate('/app/inventory');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to create inventory', 'error');
    },
  });

  const onSubmit = (data: InventoryFormData) => {
    createMutation.mutate(data);
  };

  // Reset location when type changes
  const handleLocationTypeChange = (newType: 'store' | 'warehouse') => {
    setValue('locationType', newType);
    setValue('locationId', '');
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Add Inventory
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Product Selection */}
            <Controller
              name="productId"
              control={control}
              rules={{ required: 'Product is required' }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={products}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? products.find((p) => p.id === option)?.name || ''
                      : option.name
                  }
                  value={products.find((p) => p.id === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id || '');
                  }}
                  loading={productsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Product"
                      error={!!errors.productId}
                      helperText={errors.productId?.message}
                      margin="normal"
                      fullWidth
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              )}
            />

            {/* Location Type Selection */}
            <FormControl fullWidth margin="normal" error={!!errors.locationType}>
              <InputLabel>Location Type</InputLabel>
              <Select
                value={locationType}
                label="Location Type"
                onChange={(e) => handleLocationTypeChange(e.target.value as 'store' | 'warehouse')}
              >
                <MenuItem value="store">Store</MenuItem>
                <MenuItem value="warehouse">Warehouse</MenuItem>
              </Select>
              {errors.locationType && (
                <FormHelperText>{errors.locationType.message}</FormHelperText>
              )}
            </FormControl>

            {/* Location Selection */}
            <Controller
              name="locationId"
              control={control}
              rules={{ required: 'Location is required' }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={locations}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? locations.find((l) => l.id === option)?.name || ''
                      : option.name
                  }
                  value={locations.find((l) => l.id === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id || '');
                  }}
                  loading={locationsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={locationType === 'store' ? 'Store' : 'Warehouse'}
                      error={!!errors.locationId}
                      helperText={errors.locationId?.message}
                      margin="normal"
                      fullWidth
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {locationsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              )}
            />

            {/* Initial Quantity */}
            <TextField
              {...register('initialQuantity', {
                valueAsNumber: true,
                min: { value: 0, message: 'Quantity must be positive' },
              })}
              error={!!errors.initialQuantity}
              helperText={errors.initialQuantity?.message}
              margin="normal"
              fullWidth
              label="Initial Quantity"
              type="number"
              inputProps={{ min: 0 }}
            />

            {/* Low Stock Threshold */}
            <TextField
              {...register('lowStockThreshold', {
                valueAsNumber: true,
                min: { value: 0, message: 'Threshold must be positive' },
              })}
              error={!!errors.lowStockThreshold}
              helperText={errors.lowStockThreshold?.message || 'Alert when stock falls below this level'}
              margin="normal"
              fullWidth
              label="Low Stock Threshold"
              type="number"
              inputProps={{ min: 0 }}
            />

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Inventory'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/app/inventory')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
